import { Action } from 'redux';

import { CANCEL_ACTION_BAR_ACTIVITY, START_ACTIVITY } from 'store/modules/activity';
import {
  CONTENT_TOO_LARGE,
  FORBIDDEN,
  FORM_HTTP_ERRORS,
  METHOD_NOT_ALLOWED,
  NOT_FOUND,
  UNPROCESSABLE_CONTENT,
} from 'store/modules/network';
import { LOCATION_CHANGE } from 'store/modules/router';

import { getType } from 'utils/get-type';
import { containsUuid } from 'utils/string';

// Actions
//======================================================================================================================

export const RESET_ERROR = 'RESET_ERROR';

// Action Creators
//======================================================================================================================

export function resetError() {
  return { type: RESET_ERROR };
}

// Reducer
//======================================================================================================================

type ErrorsState = ErrorT[];

type ErrorsAction = Action<string> &
  (
    | { payload: { failureType: string; errors: ErrorT[] }; error: boolean }
    | { payload: ErrorT[]; error: boolean }
    | { payload: ErrorT; error: boolean }
    | { payload: null; error: boolean }
  );

export const initialState = [];

function createSetErrorsReducer({
  handlerType = 'unhandled',
  errorMapFunction,
}: {
  handlerType?: ErrorHandlerType;
  errorMapFunction?: (rawError: ErrorT) => ErrorT;
}) {
  const reducer = (_state: ErrorsState, action: ErrorsAction): ErrorsState => {
    const { payload, error: showError } = action;
    const failureType = payload && 'failureType' in payload ? payload.failureType : undefined;

    if (!payload || !showError) return [];

    const errors = Array.isArray(payload) ? payload : 'errors' in payload ? payload.errors : [payload];
    const mappedErrors = errors.map(errorMapFunction || ((rawError) => ({ ...rawError, handlerType, failureType })));

    return mappedErrors;
  };

  return reducer;
}

const FORM_HANDLER_ACTIONS = new Set<string>();
const UUID_FORM_HANDLER_ACTIONS = new Set<string>();
const IGNORE_HANDLER_ACTIONS = new Set<string>();

export function registerFormErrorAction<T extends string>(action: T): T {
  FORM_HANDLER_ACTIONS.add(action);

  return action;
}

export function registerUuidFormErrorAction<T extends string>(action: T): T {
  UUID_FORM_HANDLER_ACTIONS.add(action);

  return action;
}

export function registerIgnoreErrorAction<T extends string>(action: T): T {
  IGNORE_HANDLER_ACTIONS.add(action);

  return action;
}

export function errorsReducer(state: ErrorsState = initialState, action: ErrorsAction): ErrorsState {
  const { type, payload } = action;
  const failureType = payload && 'failureType' in payload ? payload.failureType : undefined;

  switch (true) {
    case FORM_HANDLER_ACTIONS.has(type):
    case FORM_HTTP_ERRORS.includes(type) && failureType && FORM_HANDLER_ACTIONS.has(failureType): {
      const reducer = createSetErrorsReducer({ handlerType: 'form' });

      return reducer(state, action);
    }

    case UUID_FORM_HANDLER_ACTIONS.has(type):
    case FORM_HTTP_ERRORS.includes(type) && failureType && UUID_FORM_HANDLER_ACTIONS.has(failureType): {
      const errorMapFunction = (rawError: ErrorT): ErrorT => {
        if (!rawError) return { handlerType: 'form' };

        return {
          ...rawError,
          source: rawError.source && containsUuid(rawError.source) ? `identifier_${rawError.source}` : rawError.source,
          handlerType: 'form',
        };
      };
      const reducer = createSetErrorsReducer({ errorMapFunction });

      return reducer(state, action);
    }

    case IGNORE_HANDLER_ACTIONS.has(type): {
      return state;
    }

    case type === FORBIDDEN: {
      const reducer = createSetErrorsReducer({ handlerType: 'http_forbidden' });

      return reducer(state, action);
    }

    case type === NOT_FOUND: {
      const reducer = createSetErrorsReducer({ handlerType: 'http_not_found' });

      return reducer(state, action);
    }

    case type === UNPROCESSABLE_CONTENT: {
      const reducer = createSetErrorsReducer({ handlerType: 'http_unprocessable_content' });

      return reducer(state, action);
    }

    case type === METHOD_NOT_ALLOWED: {
      const reducer = createSetErrorsReducer({ handlerType: 'http_method_not_allowed' });

      return reducer(state, action);
    }

    case type === CONTENT_TOO_LARGE: {
      const reducer = createSetErrorsReducer({ handlerType: 'http_content_too_large' });

      return reducer(state, action);
    }

    case type === LOCATION_CHANGE:
    case type === RESET_ERROR: {
      return initialState;
    }

    case type.endsWith('SUCCESS'): {
      if (state.length === 0) return initialState;

      return state.filter(({ failureType }) => failureType && getType(failureType) !== getType(type));
    }

    case type.endsWith('FAILURE') || type.endsWith('ERROR'): {
      const reducer = createSetErrorsReducer({});

      return reducer(state, action);
    }

    case type === CANCEL_ACTION_BAR_ACTIVITY:
    case type === START_ACTIVITY: {
      return initialState;
    }

    default: {
      return state;
    }
  }
}
